import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { closeModal, openModal } from "react-url-modal";
import { Button, Modal } from "@/components";
import { GamesItem } from "../../components/GamesItem";
import { JoinGameContent } from "./components/JoinGameContent/JoinGameContent";
import { CreateGameContent } from "./components/CreateGameContent/CreateGameContent";
import { DeleteGameAndTournamentModal } from "./components/DeleteGameAndTournamentModal/DeleteGameAndTournamentModal";
import { ModalLevelInfo } from "./components/ModalLevelInfo/ModalLevelInfo";
import { CancelRequestModal } from "../../components/CancelRequestModal";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { getTokenFromStorage } from "@/utils";
import { openAuthModal } from "@/utils/authModal/openAuthModal";
import {
  selectGames,
  selectAppliedGames,
  selectCreatedGames,
  fetchGetGameById,
  fetchGetGamesList,
  fetchGetAppliedGames,
  fetchGetCreatedGames,
  fetchMarkApplicationsAsSeen,
  fetchCancelApplication,
  fetchGetApplicationsCount,
  setGames,
} from "@/redux/games";
import { IGame } from "@/redux/games/types";
import styles from "./JoinGameModal.module.scss";

interface JoinGameModalProps {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  params?: {
    idGame?: string;
    isCreateGame?: string;
    isAppliedGame?: string;
    fromDeeplink?: string;
  };
}

const JoinGameModalComponent = ({
  setIsOpen,
  params = {},
}: JoinGameModalProps): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const idGame = params?.idGame ? parseInt(params.idGame, 10) : null;
  const isCreateGame = params?.isCreateGame === "true";
  const isAppliedGame = params?.isAppliedGame === "true";
  const fromDeeplink = params?.fromDeeplink === "true";

  const games = useAppSelector(selectGames);
  const appliedGames = useAppSelector(selectAppliedGames);
  const createdGames = useAppSelector(selectCreatedGames);

  const [game, setGame] = useState<IGame | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [levelInfoModalVisible, setLevelInfoModalVisible] = useState(false);
  const [selectedLevelInfo, setSelectedLevelInfo] = useState<string>("");

  const isGamePast = (): boolean => {
    if (!game?.date) return false;
    try {
      const endTime = game.endTime || '23:59';
      const [hours, minutes] = endTime.split(':').map(Number);
      const gameEnd = new Date(game.date);
      gameEnd.setHours(hours, minutes, 0, 0);
      return new Date() > gameEnd;
    } catch (error) {
      return false;
    }
  };

  const handleClose = () => {
    if (fromDeeplink) {
      navigate("/padel/games");
    }
    closeModal();
    if (setIsOpen) {
      setIsOpen(false);
    }
  };

  // Проверка авторизации при открытии модалки
  useEffect(() => {
    const token = getTokenFromStorage();
    if (!token) {
      if (fromDeeplink) {
        navigate("/padel/games");
      }
      closeModal();
      if (setIsOpen) {
        setIsOpen(false);
      }
      openAuthModal(dispatch);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!idGame) {
      handleClose();
      return;
    }

    const loadGame = async () => {
      setIsLoading(true);
      try {
        let currentGame: IGame | null = null;

        if (isCreateGame) {
          // Ищем в созданных играх
          currentGame = createdGames.find((g) => g.id === idGame) || null;
          
          if (currentGame?.applications?.length) {
            // Отмечаем заявки как просмотренные
            const applicationIds = currentGame.applications.map((app) => app.id);
            await dispatch(
              fetchMarkApplicationsAsSeen({ applicationIds })
            ).unwrap();
          }
        } else {
          // Ищем в доступных играх
          currentGame = games.find((g) => g.id === idGame) || null;

          // Если не найдена, ищем в играх с заявками
          if (!currentGame) {
            currentGame = appliedGames.find((g) => g.id === idGame) || null;
          }
        }

        // Если игра не найдена, загружаем с сервера
        if (!currentGame) {
          try {
            currentGame = await dispatch(fetchGetGameById(idGame)).unwrap();
          } catch (error) {
            // Если не удалось загрузить, обновляем списки
            await Promise.all([
              dispatch(fetchGetGamesList()),
              dispatch(fetchGetAppliedGames()),
              dispatch(fetchGetCreatedGames()),
            ]);
            // Пробуем найти снова
            if (isCreateGame) {
              currentGame = createdGames.find((g) => g.id === idGame) || null;
            } else {
              currentGame = games.find((g) => g.id === idGame) || null;
              if (!currentGame) {
                currentGame = appliedGames.find((g) => g.id === idGame) || null;
              }
            }
          }
        }

        setGame(currentGame);
      } catch (error) {
        console.error("Error loading game:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idGame, isCreateGame]);

  if (isLoading) {
    return (
      <Modal
        title="Загрузка..."
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={600}
      >
        <div className={styles.loading}>Загрузка игры...</div>
      </Modal>
    );
  }

  if (!game) {
    return (
      <Modal
        title="Ошибка"
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={600}
      >
        <div className={styles.error}>Игра не найдена</div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        title={isCreateGame ? "Управление игрой" : isAppliedGame ? "Информация об игре" : "Присоединиться к игре"}
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={500}
        mobileContentSize="full-screen"
      >
        <div className={styles.wrapper}>
          <div className={styles.gameItemTop}>
            <GamesItem
              item={game}
              button={false}
              setIsRequiredAuthOpened={() => {}}
              modalWindow
            />
          </div>

          <div className={styles.scrollContent}>
            {isCreateGame ? (
              <CreateGameContent game={game} />
            ) : !isAppliedGame ? (
              <JoinGameContent
                game={game}
                onLevelInfoClick={(level) => {
                  setSelectedLevelInfo(level);
                  setLevelInfoModalVisible(true);
                }}
              />
            ) : null}
          </div>

          {isCreateGame && !isGamePast() && (
            <div className={styles.actionsBottom}>
              <Button
                onClickFunc={() => setDeleteModalVisible(true)}
                newStyles={styles.deleteButton}
              >
                Удалить игру
              </Button>
            </div>
          )}

          {isAppliedGame && (
            <div className={styles.actionsBottom}>
              <Button
                newStyles={styles.buttonTransparent}
                onClickFunc={() => setCancelModalVisible(true)}
              >
                Отменить заявку
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {deleteModalVisible && idGame && (
        <DeleteGameAndTournamentModal
          isOpen={deleteModalVisible}
          setIsOpen={setDeleteModalVisible}
          gameId={idGame}
          date={game?.date}
          onSuccess={handleClose}
        />
      )}

      {cancelModalVisible && idGame && (
        <CancelRequestModal
          isOpen={cancelModalVisible}
          setIsOpen={setCancelModalVisible}
          onConfirm={async () => {
            try {
              await dispatch(fetchCancelApplication(idGame)).unwrap();
              // Обновляем список игр с заявками и счетчик
              const [applied] = await Promise.all([
                dispatch(fetchGetAppliedGames()).unwrap(),
                dispatch(fetchGetApplicationsCount()).unwrap(),
              ]);
              // Обновляем список доступных игр, добавляя отмененную игру
              const allGames = await dispatch(fetchGetGamesList()).unwrap();
              const filtered = allGames.filter(
                (game) => !applied.some((appliedGame) => appliedGame.id === game.id)
              );
              dispatch(setGames(filtered));
              handleClose();
              
              // Открываем модалку успешной отмены заявки
              openModal({ name: "successCancelRequest" });
            } catch (error) {
              console.error("Error cancelling application:", error);
            }
          }}
          isLoading={false}
        />
      )}

      <ModalLevelInfo
        isOpen={levelInfoModalVisible}
        setIsOpen={setLevelInfoModalVisible}
        level={selectedLevelInfo}
      />
    </>
  );
};

// Экспорт для react-url-modal
export default JoinGameModalComponent;
export const JoinGameModal = JoinGameModalComponent;
